import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchDoctorProfile } from '../../../../redux/middleware/user/doctor/profile.js';

// Material UI imports
import {
	Box,
	Card,
	CardContent,
	Typography,
	Grid,
	Button,
	CircularProgress,
	Container,
	Chip,
	Avatar,
	Divider,
	Paper,
} from '@mui/material';

// Material UI Icons
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PeopleIcon from '@mui/icons-material/People';
import WarningIcon from '@mui/icons-material/Warning';
import MessageIcon from '@mui/icons-material/Message';
import RateReviewIcon from '@mui/icons-material/RateReview';

const DoctorDashboard = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);

	// Get the doctor profile from Redux state
	const { user = {}, loading } =
		useSelector((state) => state.doctorProfile) || {};

	// Check if user is verified
	const isVerified =
		user?.is_verified ?? localStorage.getItem('pendingApproval') !== 'true';

	useEffect(() => {
		// Fetch profile data if not already loaded
		const fetchProfile = async () => {
			if (!user || Object.keys(user).length === 0) {
				try {
					await dispatch(fetchDoctorProfile());
				} catch (err) {
					console.error('Error fetching profile:', err);
				} finally {
					setIsLoading(false);
				}
			} else {
				setIsLoading(false);
			}
		};

		fetchProfile();
	}, [dispatch, user]);

	// Redirect if not verified
	useEffect(() => {
		if (!isVerified) {
			navigate('/app/doctor/pending/approval');
		}
	}, [isVerified, navigate]);

	// Get first name for personalized greeting
	const firstName = user?.full_name?.split(' ')[0] || 'Doctor';

	// Format current date for dashboard header
	const today = new Date();
	const options = { weekday: 'long', month: 'long', day: 'numeric' };
	const formattedDate = today.toLocaleDateString('en-US', options);

	const ProfileCard = ({ icon, label, value }) => (
		<Card elevation={2}>
			<CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
				<Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>{icon}</Avatar>
				<Box>
					<Typography variant="caption" color="text.secondary">
						{label}
					</Typography>
					<Typography variant="body1" fontWeight="medium">
						{value || 'N/A'}
					</Typography>
				</Box>
			</CardContent>
		</Card>
	);

	if (isLoading || loading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '80vh',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			{/* Header Section */}
			<Card sx={{ mb: 4 }}>
				<CardContent
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box>
						<Typography variant="h4" fontWeight="bold" gutterBottom>
							Welcome back, {firstName}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{formattedDate}
						</Typography>
					</Box>
					{user?.is_verified && (
						<Chip
							label="Verified"
							color="success"
							size="small"
							variant="outlined"
						/>
					)}
				</CardContent>
			</Card>

			{/* Profile Information */}
			<Box sx={{ mb: 4 }}>
				<Typography variant="h5" gutterBottom fontWeight="medium">
					Your Profile
				</Typography>

				{user && Object.keys(user).length > 0 ? (
					<Grid container spacing={3}>
						<Grid item xs={12} md={4}>
							<ProfileCard
								icon={<PersonIcon />}
								label="Full Name"
								value={user.full_name}
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<ProfileCard
								icon={<WorkIcon />}
								label="Professional Title"
								value={user.title}
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<ProfileCard
								icon={<EmailIcon />}
								label="Email Address"
								value={user.email}
							/>
						</Grid>
					</Grid>
				) : (
					<Card sx={{ textAlign: 'center', p: 3 }}>
						<WarningIcon color="action" sx={{ fontSize: 48, mb: 2 }} />
						<Typography variant="h6" color="text.secondary" gutterBottom>
							No profile data available
						</Typography>
						<Button variant="contained" color="primary" sx={{ mt: 2 }}>
							Complete Your Profile
						</Button>
					</Card>
				)}
			</Box>

			{/* Quick Actions */}
			<Box sx={{ mb: 4 }}>
				<Typography variant="h5" gutterBottom fontWeight="medium">
					Quick Actions
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={4}>
						<Button
							variant="outlined"
							color="primary"
							fullWidth
							size="large"
							startIcon={<EditIcon />}
							sx={{ py: 1.5 }}
						>
							Edit Profile
						</Button>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Button
							variant="outlined"
							color="success"
							fullWidth
							size="large"
							startIcon={<ScheduleIcon />}
							sx={{ py: 1.5 }}
						>
							Manage Schedule
						</Button>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Button
							variant="outlined"
							color="secondary"
							fullWidth
							size="large"
							startIcon={<PeopleIcon />}
							sx={{ py: 1.5 }}
						>
							View Patients
						</Button>
					</Grid>
				</Grid>
			</Box>

			{/* Stats Summary */}
			<Box>
				<Typography variant="h5" gutterBottom fontWeight="medium">
					At a Glance
				</Typography>
				<Grid container spacing={3}>
					<Grid item xs={6} sm={3}>
						<Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
							<Typography variant="h4" color="primary" fontWeight="bold">
								0
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Today's Appointments
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={6} sm={3}>
						<Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
							<Typography variant="h4" color="success.main" fontWeight="bold">
								0
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Patients
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={6} sm={3}>
						<Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
							<Typography variant="h4" color="secondary.main" fontWeight="bold">
								0
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Messages
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={6} sm={3}>
						<Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
							<Typography variant="h4" color="warning.main" fontWeight="bold">
								0
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Pending Reviews
							</Typography>
						</Paper>
					</Grid>
				</Grid>
			</Box>
		</Container>
	);
};

export default DoctorDashboard;
